import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from 'reactstrap';
import PasswordStrengthMeter from '../../components/passwordStrengthMeter/PasswordStrengthMeter';

import {
  startChangePassword,
  profileUnsetInitialFlag
} from '../../actions/profile';
import FloatingModal from '../../components/Modal/FloatingModal';

const ChangePasswordModal = ({
  initialPassword,
  toggleModal,
  translate,
  startChangePassword,
  profileUnsetInitialFlag,
  isModalOpen
}) => {
  const [passwordState, setPasswordState] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
    passwordSaved: false,
    message: ''
  });

  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = event => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    setPasswordState(prev => ({
      ...prev,
      [name]: value,
      message: ''
    }));
  };

  const handleCancel = () => {
    toggleModal();
  };

  const onSubmit = () => {
    const { passwordSaved, message, ...changePassword } = passwordState;

    // Prevent submission if password is invalid
    if (!isPasswordValid || !newPassword) {
      setPasswordState(prev => ({
        ...prev,
        message: translate('user_management_password_requirements_not_met')
      }));
      return;
    }

    if (changePassword.newPassword !== changePassword.confirmPassword) {
      setPasswordState(prev => ({
        ...prev,
        message: translate('user_management_password_error_match')
      }));
      return;
    }

    startChangePassword(changePassword)
      .then(() => {
        setPasswordState(prev => ({
          ...prev,
          message: translate('user_management_password_change_success'),
          passwordSaved: true
        }));

        if (initialPassword) profileUnsetInitialFlag();
      })
      .catch(() => {
        setPasswordState(prev => ({
          ...prev,
          message: translate('user_management_password_error')
        }));
      });
  };

  const { oldPassword, newPassword, confirmPassword, passwordSaved, message } =
    passwordState;

  return (
    <FloatingModal
      isOpen={isModalOpen}
      toggleModal={toggleModal}
      header={translate('user_management_change_password')}
      translate={translate}
      onCancel={!passwordSaved ? handleCancel : null}
      onConfirm={!passwordSaved ? onSubmit : null}
      confirmLabel={
        passwordSaved ? translate('general_close') : translate('general_save')
      }
      confirmDisabled={!passwordSaved && (!isPasswordValid || !newPassword)}
      cancelLabel={
        !initialPassword && !passwordSaved ? translate('general_cancel') : null
      }
      size="small"
    >
      <section>
        <Form>
          <Card>
            {message && (
              <CardHeader>
                <p>{message}</p>
              </CardHeader>
            )}
            {!passwordSaved && (
              <CardBody>
                <FormGroup>
                  <span className="modal-setting-title">
                    {translate('user_management_old_password')}
                  </span>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="fa fa-lock" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type={showOldPassword ? 'text' : 'password'}
                      name="oldPassword"
                      placeholder={translate('user_management_old_password')}
                      value={oldPassword}
                      onChange={handleInputChange}
                      required
                    />
                    <InputGroupAddon addonType="append">
                      <InputGroupText
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        style={{ cursor: 'pointer' }}
                      >
                        <i
                          className={
                            showOldPassword ? 'far fa-eye-slash' : 'far fa-eye'
                          }
                        />
                      </InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                </FormGroup>
                <FormGroup>
                  <span className="modal-setting-title">
                    {translate('user_management_new_password')}
                  </span>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="fa fa-lock" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      name="newPassword"
                      placeholder={translate('user_management_new_password')}
                      value={newPassword}
                      onChange={handleInputChange}
                      required
                    />
                    <InputGroupAddon addonType="append">
                      <InputGroupText
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{ cursor: 'pointer' }}
                      >
                        <i
                          className={
                            showNewPassword ? 'far fa-eye-slash' : 'far fa-eye'
                          }
                        />
                      </InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                </FormGroup>
                <FormGroup>
                  <span className="modal-setting-title">
                    {translate('user_management_confirm_password')}
                  </span>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="fa fa-lock" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      placeholder={translate(
                        'user_management_confirm_password'
                      )}
                      value={confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                    <InputGroupAddon addonType="append">
                      <InputGroupText
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        <i
                          className={
                            showConfirmPassword
                              ? 'far fa-eye-slash'
                              : 'far fa-eye'
                          }
                        />
                      </InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                </FormGroup>
                <PasswordStrengthMeter
                  password={newPassword}
                  translate={translate}
                  showRequirements={true}
                  onValidityChange={setIsPasswordValid}
                  repeatPassword={confirmPassword}
                />
              </CardBody>
            )}
          </Card>
        </Form>
      </section>
    </FloatingModal>
  );
};

const mapDispatchToProps = dispatch => ({
  startChangePassword: data => dispatch(startChangePassword(data)),
  profileUnsetInitialFlag: () => dispatch(profileUnsetInitialFlag())
});

export default connect(null, mapDispatchToProps)(ChangePasswordModal);
